import React, { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Loader } from "lucide-react";

// Core/Public - Load immediately
import IntroLoader from "./components/IntroLoader";
import PageTransition from "./components/PageTransition";
import PublicExperience from "./pages/public/PublicExperience";
import EternityPortal from "./pages/public/EternityPortalNew";
import DesktopPortalLayout from "./pages/public/DesktopPortalLayout";
import BrandExperience from "./pages/services/BrandExperience";
import LoginPage from "./pages/LoginPage";
import IntelligenceLayout from "./components/intelligence/IntelligenceLayout";
import { authService } from "./services/authService";

// Intelligence Core - Load with initial bundle
import HomeSimplified from "./pages/intelligence/HomeSimplified";
import ProfileMe from "./pages/intelligence/ProfileMe";

// Lazy-loaded modules - Load on demand
const AIChatGPT = lazy(() => import("./pages/intelligence/AIChatGPT"));
const ClientsHome = lazy(() => import("./pages/intelligence/ClientsHome"));
const WebsiteStudio = lazy(() => import("./pages/website/WebsiteStudio"));
const TeamManagement = lazy(() => import("./pages/intelligence/TeamManagement"));
const PermissionsManagement = lazy(() => import("./pages/intelligence/PermissionsManagement"));
const UserProfile = lazy(() => import("./pages/intelligence/UserProfile"));
const MyWork = lazy(() => import("./pages/intelligence/MyWork"));
const MyActivity = lazy(() => import("./pages/intelligence/MyActivity"));
const AppSettings = lazy(() => import("./pages/intelligence/AppSettings"));

// Client pages - Lazy load
const ClientCreate = lazy(() => import("./pages/intelligence/ClientCreate"));
const ClientProfileHome = lazy(() => import("./pages/intelligence/ClientProfileHome"));
const ClientServicesPage = lazy(() => import("./pages/intelligence/ClientServicesPage"));
const ClientProjectsPage = lazy(() => import("./pages/intelligence/ClientProjectsPage"));
const ClientMediaPage = lazy(() => import("./pages/intelligence/ClientMediaPage"));
const ClientCalendarPage = lazy(() => import("./pages/intelligence/ClientCalendarPage"));
const ClientApprovalsPage = lazy(() => import("./pages/intelligence/ClientApprovalsPage"));
const ClientReportsPage = lazy(() => import("./pages/intelligence/ClientReportsPage"));
const ClientActivityPage = lazy(() => import("./pages/intelligence/ClientActivityPage"));
const ClientIntelligencePage = lazy(() => import("./pages/intelligence/ClientIntelligencePage"));

// Loading fallback
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0B0C' }}>
    <Loader size={32} className="animate-spin" style={{ color: '#FF6129' }} />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Wrapper component to handle page transitions
const AnimatedRoutes = () => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
    
    // Scroll to top on route change
    window.scrollTo(0, 0);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <AnimatePresence mode="wait">
        {isTransitioning && <PageTransition key="page-transition" />}
      </AnimatePresence>
      
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<DesktopPortalLayout />} />
          <Route path="/services/brand" element={<BrandExperience />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* Eternity Intelligence - Nueva experiencia principal */}
          <Route path="/intelligence" element={
            <ProtectedRoute>
              <IntelligenceLayout />
            </ProtectedRoute>
          }>
            <Route index element={<HomeSimplified />} />
            <Route path="clients" element={<Suspense fallback={<LoadingFallback />}><ClientsHome /></Suspense>} />
            <Route path="clients/create" element={<Suspense fallback={<LoadingFallback />}><ClientCreate /></Suspense>} />
            <Route path="clients/:clientId" element={<Suspense fallback={<LoadingFallback />}><ClientProfileHome /></Suspense>} />
            <Route path="clients/:clientId/services" element={<Suspense fallback={<LoadingFallback />}><ClientServicesPage /></Suspense>} />
            <Route path="clients/:clientId/projects" element={<Suspense fallback={<LoadingFallback />}><ClientProjectsPage /></Suspense>} />
            <Route path="clients/:clientId/media" element={<Suspense fallback={<LoadingFallback />}><ClientMediaPage /></Suspense>} />
            <Route path="clients/:clientId/calendar" element={<Suspense fallback={<LoadingFallback />}><ClientCalendarPage /></Suspense>} />
            <Route path="clients/:clientId/approvals" element={<Suspense fallback={<LoadingFallback />}><ClientApprovalsPage /></Suspense>} />
            <Route path="clients/:clientId/reports" element={<Suspense fallback={<LoadingFallback />}><ClientReportsPage /></Suspense>} />
            <Route path="clients/:clientId/activity" element={<Suspense fallback={<LoadingFallback />}><ClientActivityPage /></Suspense>} />
            <Route path="clients/:clientId/intelligence" element={<Suspense fallback={<LoadingFallback />}><ClientIntelligencePage /></Suspense>} />
            <Route path="ask" element={<Suspense fallback={<LoadingFallback />}><AIChatGPT /></Suspense>} />
            <Route path="website" element={<Suspense fallback={<LoadingFallback />}><WebsiteStudio /></Suspense>} />
            <Route path="me" element={<ProfileMe />} />
            <Route path="me/profile" element={<Suspense fallback={<LoadingFallback />}><UserProfile /></Suspense>} />
            <Route path="me/work" element={<Suspense fallback={<LoadingFallback />}><MyWork /></Suspense>} />
            <Route path="me/activity" element={<Suspense fallback={<LoadingFallback />}><MyActivity /></Suspense>} />
            <Route path="me/team" element={<Suspense fallback={<LoadingFallback />}><TeamManagement /></Suspense>} />
            <Route path="me/permissions" element={<Suspense fallback={<LoadingFallback />}><PermissionsManagement /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><AppSettings /></Suspense>} />
          </Route>
        </Routes>
      </AnimatePresence>
    </>
  );
};

function App() {
  const [showIntro, setShowIntro] = useState(false); // Disabled for Phase 1 testing
  const [isLoading, setIsLoading] = useState(false);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setIsLoading(false);
  };

  return (
    <div className="App">
      {/* Intro Loader - Shows once per session */}
      {showIntro && <IntroLoader onComplete={handleIntroComplete} />}
      
      {/* Main App */}
      {!isLoading && (
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      )}
    </div>
  );
}

export default App;
